import { Button } from '@kerfjs/ui/button';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { List } from '@kerfjs/ui/list';
import { ListItem } from '@kerfjs/ui/list-item';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import { type SafeHtml } from 'kerfjs';

import { LabeledSpinner } from '../../../components/labeled-spinner.js';
import { sourceRowPicker } from '../../../data/state.js';
import { sourceRowProvenance } from '../../../lib/source-row-provenance.js';

/**
 * Find any inventory or purchase row for one demand line (HS2-4TER5Q), including
 * rows the similarity score did not suggest. Choosing a row confirms the match
 * through the same route as the suggested-candidate Match button.
 */
export function SourceRowPickerDialog({
  lineDescription,
  picker = sourceRowPicker.value,
}: {
  lineDescription: string;
  /** The picker state; the live signal unless a catalog demo supplies a fixture. */
  picker?: (typeof sourceRowPicker)['value'];
}): SafeHtml {
  if (picker === null) return <></>;
  const rows = picker.result?.rows ?? [];
  const total = picker.result?.total ?? 0;
  return (
    <DialogSurface
      label="Find a Source Row"
      className="source-row-picker-dialog"
      lightDismiss
      rootAttributes={{ 'data-line-item-id': picker.lineItemId, 'data-auto-open-dialog': '' }}
      footer={
        <Button
          tone="neutral"
          size="default"
          emphasis="plain"
          action="source-row-picker-close"
          rootAttributes={{ 'data-dialog-close': '' }}
        >
          Close
        </Button>
      }
    >
      <List gap="m" controlInsets="rl">
        <Text size="compact" tone="quiet">
          For: {lineDescription}
        </Text>
        <TextField
          name="source-row-query"
          type="search"
          label="Search Descriptions, SKUs, and Vendors"
          value={picker.query}
          autofocus
          clearable
        />
        {picker.error !== null ? <StateBanner tone="danger" title={picker.error} /> : null}
        {picker.loading && picker.result === null ? <LabeledSpinner label="Searching rows" quiet /> : null}
        {picker.result !== null && rows.length === 0 ? (
          <EmptyState
            title="No Matching Rows"
            detail={
              picker.query.trim() === '' ? 'No inventory or purchase rows apply to this demand.' : 'Try fewer words.'
            }
          />
        ) : null}
        {rows.length > 0 ? (
          <List className="source-row-results" aria-busy={picker.loading ? 'true' : undefined}>
            {rows.map((row) => (
              <ListItem
                label={row.description}
                description={sourceRowProvenance(row)}
                status={row.status === 'confirmed' ? 'Matched' : row.status === 'rejected' ? 'Rejected' : undefined}
                action="source-row-pick"
                itemId={`${row.kind}:${row.recordId}`}
                disabled={row.status === 'confirmed'}
                multiline
                rootAttributes={{ 'data-key': row.recordId }}
              />
            ))}
          </List>
        ) : null}
        {total > rows.length ? (
          <Text size="compact" tone="quiet">
            Showing the closest {String(rows.length)} of {String(total)} rows. Search to narrow them.
          </Text>
        ) : null}
      </List>
    </DialogSurface>
  );
}
